import Editor from "@draft-js-plugins/editor";
import createImagePlugin from "@draft-js-plugins/image";
import { Image, VideoLibrary } from "@mui/icons-material";
import { Alert, AlertTitle } from "@mui/material";
import {
  EditorState,
  RichUtils,
  convertToRaw,
  DraftHandleValue,
  RawDraftContentState,
  convertFromRaw,
  CompositeDecorator,
  AtomicBlockUtils,
  DraftInlineStyle,
  ContentBlock,
} from "draft-js";
import "draft-js-image-plugin/lib/plugin.css";
import "draft-js/dist/Draft.css";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import React, { useCallback, useEffect, useState } from "react";
import useEvent from "react-use-event-hook";
import { BulletListIcon } from "shared/theme/icons/BulletListIcon";
import { HeadingOneIcon } from "shared/theme/icons/HeadingOneIcon";
import { HeadingThreeIcon } from "shared/theme/icons/HeadingThreeIcon";
import { HeadingTwoIcon } from "shared/theme/icons/HeadingTwoIcon";
import { NumericListIcon } from "shared/theme/icons/NumericListIcon";
import { RegularTextIcon } from "shared/theme/icons/RegularTextIcon";
import { COLOR_BLACK } from "../../theme/colors";
import { BoldIcon } from "../../theme/icons/BoldIcon";
import { FaceHappyIcon } from "../../theme/icons/FaceHappy";
import { ItalicIcon } from "../../theme/icons/ItalicIcon";
import { LinkIcon } from "../../theme/icons/LinkIcon";
import { TableIcon } from "../../theme/icons/TableIcon";
import FieldLabel from "../Form/components/FieldLabel";
import { EmojiContent } from "./components/EmojiContent";
import ImageComponent from "./components/ImageContent";
import ImageSkeleton from "./components/ImageSkeleton";
import Link from "./components/Link";
import ToolbarItem from "./components/ToolbarItem";
import VideoContent from "./components/VideoContent";
import {
  addLink,
  createBlockRendererFn,
  insertCustomBlock,
  insertEmptyBlock,
  toggleInlineStyle,
} from "./helpers";
import {
  StyledContainer,
  StyledEditorContainer,
  StyledToolbarContainer,
  StyledWrapper,
} from "./styled";
import { findLinkEntities, getVideoType, parseVideoUrl } from "./utils";

interface DraftEditorProps {
  ref?: React.RefObject<Editor>;
  content: RawDraftContentState | null;
  onChange: (content: RawDraftContentState) => void;
  placeholder?: string;
  label?: string;
  hasHeadingToolbars?: boolean;
  hasListToolbars?: boolean;
  hasEmojiToolbar?: boolean;
  hasWidgets?: boolean;
  hasImageUpload?: boolean;
  hasVideoEmbed?: boolean;
  entityName?: string;
  disabled?: boolean;
}

const CUSTOM_BLOCK_TYPE = "table-of-contents";

const decorator = new CompositeDecorator([
  {
    strategy: findLinkEntities,
    component: (props) => <Link {...props} />,
  },
]);

const imagePlugin = createImagePlugin({
  imageComponent: ImageComponent,
});
const plugins = [imagePlugin];

const DraftEditor: React.FC<DraftEditorProps> = ({
  content,
  ref,
  onChange,
  placeholder,
  label,
  hasHeadingToolbars = false,
  hasListToolbars = false,
  hasEmojiToolbar = false,
  hasWidgets = false,
  hasImageUpload = false,
  hasVideoEmbed = false,
  entityName = "",
  disabled,
}) => {
  const [editorState, setEditorState] = useState(() =>
    content
      ? EditorState.createWithContent(convertFromRaw(content), decorator)
      : EditorState.createEmpty(decorator),
  );
  const [currentInlineStyle, setCurrentInlineStyle] = useState<
    DraftInlineStyle | undefined
  >();
  const [errorMessage, setErrorMessage] = useState<{
    title: string;
    message: string;
  }>();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (content) {
      const contentState = convertFromRaw(content);
      const selection = editorState.getSelection();
      const newEditorState = EditorState.createWithContent(
        contentState,
        decorator,
      );
      setEditorState(EditorState.forceSelection(newEditorState, selection));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  useEffect(() => {
    setCurrentInlineStyle(editorState.getCurrentInlineStyle());
  }, [editorState]);

  const handleEditorChange = (state: EditorState) => {
    const contentState = state.getCurrentContent();

    const updatedState = EditorState.set(state, { decorator });
    setEditorState(updatedState);

    if (contentState.hasText()) {
      onChange(convertToRaw(contentState));
    }
  };

  const toggleBlockType = (blockType: string) => {
    if (editorState) {
      handleEditorChange(RichUtils.toggleBlockType(editorState, blockType));
    }
  };

  const handleKeyCommand = (command: string): DraftHandleValue => {
    if (editorState) {
      const newState = RichUtils.handleKeyCommand(editorState, command);
      if (newState) {
        handleEditorChange(newState);
        return "handled";
      }
    }
    return "not-handled";
  };

  const isBold = currentInlineStyle?.includes("BOLD");
  const isItalic = currentInlineStyle?.includes("ITALIC");

  const selection = editorState?.getSelection();

  const isLink =
    selection &&
    editorState
      ?.getCurrentContent()
      .getBlockForKey(selection.getStartKey())
      .getEntityAt(selection.getStartOffset()) !== null;

  const blockType =
    selection &&
    editorState
      ?.getCurrentContent()
      .getBlockForKey(selection.getStartKey())
      .getType();

  const handleAddLink = () => {
    const url = window.prompt("Enter a URL");
    if (url && editorState) {
      handleEditorChange(addLink(editorState, url));
    }
  };

  const handleInsertCustomBlock = () => {
    // Insert the custom block

    if (!editorState) return;

    const newStateWithCustomBlock = insertCustomBlock(
      editorState,
      CUSTOM_BLOCK_TYPE,
    );
    if (newStateWithCustomBlock && editorState) {
      handleEditorChange(newStateWithCustomBlock);
    }

    // Insert an empty block after the custom block
    handleEditorChange(insertEmptyBlock(newStateWithCustomBlock));
  };

  const handleAddVideo = useEvent(() => {
    const videoUrl = window.prompt("Enter video URL (YouTube, Vimeo, or Loom)");
    if (!videoUrl || !editorState) return;

    const videoType = getVideoType(videoUrl);
    if (!videoType) {
      setErrorMessage({
        title: "Invalid video URL",
        message: "Please enter a valid YouTube, Vimeo, or Loom URL",
      });
      return;
    }

    const embedUrl = parseVideoUrl(videoUrl);
    if (!embedUrl) {
      setErrorMessage({
        title: "Could not parse video URL",
        message: "Please enter a valid video URL",
      });
      return;
    }

    // Create an entity for the video
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity(
      "VIDEO",
      "IMMUTABLE",
      { src: embedUrl, videoType },
    );

    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newEditorState = AtomicBlockUtils.insertAtomicBlock(
      EditorState.set(editorState, {
        currentContent: contentStateWithEntity,
      }),
      entityKey,
      " ",
    );

    handleEditorChange(newEditorState);
    setErrorMessage(undefined);
  });

  const handleImageUpload = useEvent(() => {
    const storage = getStorage();
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/jpeg, image/png";
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      if (!["image/jpeg", "image/png"].includes(file.type)) {
        setErrorMessage({
          title: "Wrong format",
          message: "Only JPEG and PNG files are allowed",
        });
        return;
      }

      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        setErrorMessage({
          title: "Image too large",
          message: "File size must be less than 10MB",
        });
        return;
      }

      try {
        // Create a temporary entity for the skeleton with a placeholder src
        const contentState = editorState?.getCurrentContent();
        const contentStateWithEntity = contentState?.createEntity(
          "atomic",
          "IMMUTABLE",
          { isLoading: true }, // Use isLoading flag
        );

        if (!contentStateWithEntity || !editorState) return;

        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
        const newEditorState = AtomicBlockUtils.insertAtomicBlock(
          EditorState.set(editorState, {
            currentContent: contentStateWithEntity,
          }),
          entityKey,
          " ",
        );

        // Update editor state with skeleton
        handleEditorChange(newEditorState);

        // Upload the image
        const timestamp = Date.now();
        const filename = `${entityName}-images-editor/${timestamp}-${file.name}`;
        const tempStorageRef = storageRef(storage, filename);
        await uploadBytes(tempStorageRef, file);
        const imageUrl = await getDownloadURL(tempStorageRef);

        if (!imageUrl) {
          throw new Error("Failed to get download URL");
        }

        // Update the same entity with the image URL
        const contentStateWithImage = newEditorState.getCurrentContent();
        contentStateWithImage.mergeEntityData(entityKey, {
          src: imageUrl,
          isLoading: false,
        });

        const finalEditorState = EditorState.push(
          newEditorState,
          contentStateWithImage,
          "apply-entity",
        );

        handleEditorChange(finalEditorState);
        setErrorMessage(undefined);

        // This is a workaround to force the editor to update the block type and handle infinite images loading
        setTimeout(() => {
          handleEditorChange(
            RichUtils.toggleBlockType(finalEditorState, "unstyled"),
          );
        }, 5000);
      } catch (error) {
        setErrorMessage({
          title: "Upload failed",
          message: "Failed to upload image. Please try again.",
        });
        console.error("Error uploading image:", error);
      }
    };
    input.click();
  });

  const getEntityData = useCallback(
    (block: ContentBlock) => {
      const contentState = editorState?.getCurrentContent();
      const entity = block.getEntityAt(0);

      if (!entity) return null;

      const entityInstance = contentState?.getEntity(entity);
      const entityData = entityInstance?.getData();
      const entityType = entityInstance?.getType();

      return { ...entityData, entityType };
    },
    [editorState],
  );

  const handleBlockRendererFn = useEvent((block: ContentBlock) => {
    if (!editorState || !mounted) return null;

    if (block.getType() === "atomic") {
      const entityData = getEntityData(block);

      if (!entityData) return null;

      if (entityData.isLoading) {
        return {
          component: ImageSkeleton,
          editable: false,
          props: {
            onChange: handleEditorChange,
          },
        };
      }

      if (entityData.entityType === "VIDEO") {
        return {
          component: VideoContent,
          editable: false,
          props: {
            src: entityData.src,
            videoType: entityData.videoType,
            block,
            contentState: editorState.getCurrentContent(),
          },
        };
      }

      return {
        component: ImageComponent,
        editable: false,
        props: {
          getEditorState: () => editorState,
          onChange: handleEditorChange,
        },
      };
    }

    const blockRendererFn = createBlockRendererFn(
      editorState,
      handleEditorChange,
      CUSTOM_BLOCK_TYPE,
    );

    return blockRendererFn?.(block);
  });

  return (
    <>
      {errorMessage && (
        <Alert
          severity="error"
          onClose={() => setErrorMessage(undefined)}
          sx={{
            position: "absolute",
            bottom: 24,
            right: 24,
            "& .MuiAlertTitle-root": {
              fontSize: "16px",
            },
            "& .MuiAlert-message": {
              fontSize: "14px",
            },
          }}
        >
          <AlertTitle>{errorMessage.title}</AlertTitle>
          {errorMessage.message}
        </Alert>
      )}
      <StyledWrapper>
        <FieldLabel label={label} labelColor={COLOR_BLACK} />
        <StyledContainer>
          <StyledToolbarContainer>
            {hasHeadingToolbars && (
              <>
                <ToolbarItem
                  onClick={() => toggleBlockType("header-one")}
                  isHighlighted={blockType === "header-one"}
                  Icon={<HeadingOneIcon />}
                />
                <ToolbarItem
                  onClick={() => toggleBlockType("header-two")}
                  isHighlighted={blockType === "header-two"}
                  Icon={<HeadingTwoIcon size={21} />}
                />
                <ToolbarItem
                  onClick={() => toggleBlockType("header-three")}
                  isHighlighted={blockType === "header-three"}
                  Icon={<HeadingThreeIcon size={18} />}
                />
                <ToolbarItem
                  onClick={() => toggleBlockType("unstyled")}
                  isHighlighted={blockType === "unstyled"}
                  Icon={<RegularTextIcon />}
                />
              </>
            )}
            {hasListToolbars && (
              <>
                <ToolbarItem
                  onClick={() => toggleBlockType("unordered-list-item")}
                  isHighlighted={blockType === "unordered-list-item"}
                  Icon={<BulletListIcon />}
                />
                <ToolbarItem
                  onClick={() => toggleBlockType("ordered-list-item")}
                  isHighlighted={blockType === "ordered-list-item"}
                  Icon={<NumericListIcon />}
                />
              </>
            )}
            <ToolbarItem
              onClick={() =>
                editorState &&
                handleEditorChange(toggleInlineStyle(editorState, "BOLD"))
              }
              isHighlighted={isBold}
              Icon={<BoldIcon />}
            />
            <ToolbarItem
              onClick={() =>
                editorState &&
                handleEditorChange(toggleInlineStyle(editorState, "ITALIC"))
              }
              isHighlighted={isItalic}
              Icon={<ItalicIcon />}
            />
            <ToolbarItem
              onClick={handleAddLink}
              isHighlighted={isLink}
              Icon={<LinkIcon />}
            />
            {hasEmojiToolbar && (
              <ToolbarItem
                isTooltip
                tooltipTitle={<EmojiContent />}
                Icon={<FaceHappyIcon />}
              />
            )}
            {hasWidgets && (
              <ToolbarItem
                onClick={handleInsertCustomBlock}
                Icon={<TableIcon />}
              />
            )}
            {hasImageUpload && (
              <ToolbarItem onClick={handleImageUpload} Icon={<Image />} />
            )}
            {hasVideoEmbed && (
              <ToolbarItem onClick={handleAddVideo} Icon={<VideoLibrary />} />
            )}
          </StyledToolbarContainer>
          <StyledEditorContainer className="editor-container">
            {mounted && editorState && (
              <Editor
                ref={ref}
                editorState={editorState}
                handleKeyCommand={handleKeyCommand}
                onChange={handleEditorChange}
                placeholder={placeholder}
                blockRendererFn={handleBlockRendererFn}
                plugins={plugins}
                readOnly={disabled}
              />
            )}
          </StyledEditorContainer>
        </StyledContainer>
      </StyledWrapper>
    </>
  );
};

export default DraftEditor;
